/**
 * GA4 Ring Builder & Blog Upsell Tracking
 * ---------------------------------------
 * Sends structured events to GA4 (property G-9C4BEMWDT9) via gtag('event', ...).
 *
 * Ring Builder (page with the Gemist designer section):
 *   begin_ring_builder      { }                        - "START DESIGNING" clicked
 *   ring_builder_step       { step_name, step_value }  - user changed a builder control
 *   add_built_ring_to_cart  { value, currency, items_count, has_diamond,
 *                             has_matching_band, has_engraving, ring_size,
 *                             setting, metal, stone_shape, carat, diamond_origin }
 *
 * Blog articles:
 *   blog_upsell_click            { link_type, product_name, destination, article_title }
 *   blog_collection_banner_click  { product_name, destination, article_title, position }
 *
 * HOW THE RING BUILDER IS TRACKED
 * The builder is a CROSS-ORIGIN iframe (happyjewelers.gemist.co). We cannot read
 * anything inside it, so all builder signals come from the postMessage stream the
 * iframe already sends to the parent page. Gemist emits no "step" message; it
 * emits `updateParams` carrying the full ring configuration on every change
 * (setting, band, band_width, metal, stone_shape, carat, diamond, prongs).
 * We diff consecutive updateParams payloads to work out WHICH control the user
 * just changed, and report that as step_name/step_value.
 *
 * `add_built_ring_to_cart` is NOT taken from the `buy` message. The `buy` message
 * only expresses INTENT to add; the theme may still fail the actual /cart/add.js
 * call, which would over-count. Instead we observe the cart mutation itself: we
 * wrap window.fetch and, on a SUCCESSFUL (response.ok) POST to /cart/add.js whose
 * returned line items include a "Gemist Marketplace Diamond" product_type, we fire
 * once. That product type is exclusive to the ring builder (verified against the
 * live catalog and real orders), so no ordinary product add can be mistaken for a
 * ring. The event fires if and only if a built ring truly entered the cart, and
 * `value` is summed from the returned lines' final_line_price (accurate, and free
 * of any pre-existing-cart contamination because only the newly-added lines are
 * in that response). One /cart/add.js call carries all of a ring's lines, so this
 * is inherently one-event-per-add: no baseline, dedup, or cross-tab logic needed.
 *
 * These listeners are passive: the message listener never calls preventDefault or
 * mutates messages; the fetch wrapper always calls the original fetch, only ever
 * reads a clone() of the response, never alters the response the theme receives,
 * and swallows any error in its own inspection.
 *
 * NOTE ON DELIVERY: events are sent with send_to = the GA4 measurement id so they
 * reach the property directly. That config is supplied at runtime by the Shopify
 * Google & YouTube channel (the theme's GTM container was removed in 2026-07).
 * If GA4 is ever re-homed, update measurementId in snippets/ga4-custom-events.liquid.
 */
(function () {
  'use strict';

  var config = (window.GA4CustomEvents && window.GA4CustomEvents.config) || {};
  var GA4_ID = config.measurementId || 'G-9C4BEMWDT9';
  var GEMIST_ORIGIN = config.gemistOrigin || 'https://happyjewelers.gemist.co';

  function emit(eventName, params) {
    if (typeof window.gtag !== 'function') return;
    var payload = {};
    for (var k in params) {
      if (Object.prototype.hasOwnProperty.call(params, k) && params[k] !== undefined && params[k] !== '') {
        payload[k] = params[k];
      }
    }
    payload.send_to = GA4_ID;
    window.gtag('event', eventName, payload);
  }

  function text(node) {
    return (node && node.textContent ? node.textContent : '').replace(/\s+/g, ' ').trim();
  }

  /* ----------------------------------------------------------------------
   * RING BUILDER
   * -------------------------------------------------------------------- */

  function initRingBuilder() {
    var section = document.querySelector('[data-designer-wrapper]');
    if (!section) return;

    // 1. begin_ring_builder - the CTA that reveals the designer.
    //    The theme's openDesigner() is one-way and idempotent (it guards on
    //    `designerLoaded` and there is no close control), so a second click does
    //    nothing to the UI. Report the start once per page load to match, or an
    //    impatient double-click would count two builder entries in the funnel.
    var openButton = document.querySelector('[data-open-designer]');
    if (openButton) {
      var builderStarted = false;
      openButton.addEventListener('click', function () {
        if (builderStarted) return;
        builderStarted = true;
        emit('begin_ring_builder', {
          cta_text: text(openButton),
          page_path: window.location.pathname
        });
      });
    }

    // Human-readable labels for the config keys Gemist sends.
    var STEP_LABELS = {
      setting: 'Setting',
      band: 'Band',
      band_width: 'Band Width',
      metal: 'Metal',
      stone_shape: 'Shape',
      carat: 'Carat',
      diamond: 'Diamond Origin',
      prongs: 'Prongs',
      ring_size: 'Ring Size'
    };

    var previousParams = null;

    // 2 + 3. Listen to the Gemist postMessage stream.
    window.addEventListener('message', function (event) {
      if (event.origin !== GEMIST_ORIGIN) return;

      var message;
      try {
        message = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
      } catch (e) {
        return;
      }
      if (!message || !message.type) return;

      if (message.type === 'updateParams') {
        var names = message.paramNames || [];
        var current = {};
        for (var i = 0; i < names.length; i++) {
          var n = names[i];
          if (message[n] !== undefined) current[n] = message[n];
        }

        // Gemist sends `updateParams` twice on load with no config keys, then a
        // payload carrying the builder's DEFAULT selections. None of those are
        // user actions, so the first payload that actually contains config keys
        // becomes our baseline and is not reported. Only later changes are steps.
        if (previousParams === null) {
          if (Object.keys(current).length > 0) previousParams = current;
          return;
        }

        // Report every key whose value actually changed.
        for (var key in current) {
          if (!Object.prototype.hasOwnProperty.call(current, key)) continue;
          if (previousParams[key] !== current[key]) {
            // A cleared selection arrives as an empty string. emit() drops empty
            // params, which would leave step_value unset (blank in GA4 reports),
            // so label it explicitly instead.
            var value = current[key];
            emit('ring_builder_step', {
              step_name: STEP_LABELS[key] || key,
              step_key: key,
              step_value: (value === '' || value === null) ? '(cleared)' : value
            });
          }
        }
        previousParams = current;
        return;
      }
    }, false);

    // 3. add_built_ring_to_cart — fired from the real cart mutation (see below).
    watchCartAdds();
  }

  /* ----------------------------------------------------------------------
   * add_built_ring_to_cart — observed from the /cart/add.js response
   * -------------------------------------------------------------------- */

  // A completed build is added as several line items in ONE /cart/add.js call.
  // The diamond line carries product_type "Gemist Marketplace Diamond" (exclusive
  // to the builder). The setting/band/engraving lines carry _disable_click; the
  // engravings use these fixed helper variant ids (see the theme's buy handler).
  var GEMIST_DIAMOND_TYPE = 'Gemist Marketplace Diamond';
  var ENGRAVING_VARIANTS = { '47241281175778': 1, '47241277931746': 1 };

  function isGemistRingAdd(items) {
    for (var i = 0; i < items.length; i++) {
      if (items[i] && items[i].product_type === GEMIST_DIAMOND_TYPE) return true;
    }
    return false;
  }

  function ringConfigFromUrl() {
    // Gemist mirrors the live ring configuration into the parent URL query string.
    var cfg = {};
    try {
      var q = new URLSearchParams(window.location.search);
      cfg.setting = q.get('setting') || undefined;
      cfg.metal = q.get('metal') || undefined;
      cfg.stone_shape = q.get('stone_shape') || undefined;
      cfg.carat = q.get('carat') || undefined;
      cfg.diamond_origin = q.get('diamond') || undefined;
    } catch (e) {}
    return cfg;
  }

  function reportBuiltRing(items) {
    var value = 0;
    var hasDiamond = false;
    var hasEngraving = false;
    var bandCount = 0; // _disable_click lines that are a ring/band, not the setting
    var ringSize;

    for (var i = 0; i < items.length; i++) {
      var it = items[i] || {};
      var lp = typeof it.final_line_price === 'number' ? it.final_line_price
             : (typeof it.line_price === 'number' ? it.line_price : 0);
      value += lp;

      var vid = String(it.variant_id != null ? it.variant_id : it.id);
      var props = it.properties || {};
      var disableClick = props._disable_click === true || props._disable_click === 'true';
      var isEngraving = !!ENGRAVING_VARIANTS[vid];
      var isDiamond = it.product_type === GEMIST_DIAMOND_TYPE;

      if (isDiamond) hasDiamond = true;
      if (isEngraving) hasEngraving = true;
      // The setting line carries a Size property; a matching band does not.
      if (props.Size) ringSize = props.Size;
      if (disableClick && !props.Size && !isEngraving && !isDiamond) bandCount++;
    }

    var cfg = ringConfigFromUrl();
    emit('add_built_ring_to_cart', {
      value: Math.round(value) / 100,
      currency: config.currency || 'USD',
      items_count: items.length,
      has_diamond: hasDiamond,
      has_matching_band: bandCount > 0,
      has_engraving: hasEngraving,
      ring_size: ringSize,
      setting: cfg.setting,
      metal: cfg.metal,
      stone_shape: cfg.stone_shape,
      carat: cfg.carat,
      diamond_origin: cfg.diamond_origin
    });
  }

  function watchCartAdds() {
    // Install once, even if init runs twice.
    if (window.__ga4RingCartHook) return;
    var origFetch = window.fetch;
    if (typeof origFetch !== 'function') return; // no fetch => nothing to observe
    window.__ga4RingCartHook = true;

    window.fetch = function (input, init) {
      var url = '';
      try { url = typeof input === 'string' ? input : (input && input.url) || ''; } catch (e) {}
      var isCartAdd = /\/cart\/add(\.js)?(\?|#|$)/.test(url);

      var promise = origFetch.apply(this, arguments);
      if (!isCartAdd) return promise;

      // Never block or alter the theme's own handling: inspect a clone, swallow errors.
      return promise.then(function (response) {
        try {
          // Claim the response synchronously. If fetch ever ended up wrapped more
          // than once (e.g. the script evaluated twice), the stacked wrappers all
          // receive THIS SAME response object, so the flag guarantees exactly one
          // of them processes it — a single add can never be counted twice.
          if (response && response.ok && !response.__ga4RingCounted) {
            response.__ga4RingCounted = true;
            response.clone().json().then(function (data) {
              try {
                var items = data && data.items ? data.items : (data ? [data] : []);
                if (items.length && isGemistRingAdd(items)) reportBuiltRing(items);
              } catch (e) {}
            }, function () {});
          }
        } catch (e) {}
        return response;
      });
    };
  }

  /* ----------------------------------------------------------------------
   * BLOG ARTICLE UPSELLS
   * -------------------------------------------------------------------- */

  function initBlogTracking() {
    var articleTitle = config.articleTitle || document.title;

    // 1. Mid-article product upsell cards. Both buttons are tracked, and
    //    link_type distinguishes them so they stay separable in GA4.
    var cards = document.querySelectorAll('.article-featured-product');
    Array.prototype.forEach.call(cards, function (card, index) {
      var name = text(card.querySelector('.article-featured-product__title'));
      var price = text(card.querySelector('.article-featured-product__price'));

      card.addEventListener('click', function (event) {
        var link = event.target.closest ? event.target.closest('a[href]') : null;
        if (!link || !card.contains(link)) return;

        var href = link.getAttribute('href') || '';
        var isCollection = href.indexOf('/collections/') !== -1;
        var isPrimary = link.classList.contains('article-featured-product__button--primary');
        var isSecondary = link.classList.contains('article-featured-product__button--secondary');

        var linkType;
        if (isSecondary || isCollection) linkType = 'explore_similar';
        else if (isPrimary) linkType = 'view_product';
        else linkType = 'card_media'; // image / title links to the same PDP

        emit('blog_upsell_click', {
          link_type: linkType,
          product_name: name,
          product_price: price,
          destination: href,
          article_title: articleTitle,
          position: index + 1
        });
      });
    });

    // 2. End-of-article collection section ("Continue Exploring") — a product
    //    card click opens a PDP. The theme nests a Slider wrapper AND one
    //    Quickview modal per product inside the section, all of which carry the
    //    section id in their own id ("Slider-…", "Quickview-…"). Matching on
    //    id*= alone therefore binds the same click 8x. Restrict to real section
    //    wrappers (id starts with "shopify-section-") so each click fires once.
    var carousels = document.querySelectorAll('[id^="shopify-section-"][id*="featured_collection"]');
    Array.prototype.forEach.call(carousels, function (section) {
      var heading = text(section.querySelector('h1,h2,h3'));

      section.addEventListener('click', function (event) {
        var link = event.target.closest ? event.target.closest('a[href]') : null;
        if (!link || !section.contains(link)) return;

        var href = link.getAttribute('href') || '';
        if (href.indexOf('/products/') === -1) return;

        // Ignore clicks inside a Quickview modal — those open an overlay, not a PDP.
        if (link.closest && link.closest('[id^="Quickview-"]')) return;

        var cardEl = link.closest ? link.closest('.product-card') : null;
        var cards = Array.prototype.slice.call(section.querySelectorAll('.product-card'));

        emit('blog_collection_banner_click', {
          product_name: text(cardEl && cardEl.querySelector('.product-card__title')) ||
            (link.getAttribute('aria-label') || '').trim(),
          destination: href,
          section_heading: heading,
          article_title: articleTitle,
          position: cardEl ? cards.indexOf(cardEl) + 1 : undefined
        });
      });
    });
  }

  function init() {
    if (config.isRingBuilder) initRingBuilder();
    if (config.isArticle) initBlogTracking();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
